<script setup lang="ts">
import { ref, computed } from 'vue'
import Modal from '@/components/Modal.vue'
import {
    BuildingOffice2Icon,
    ChevronRightIcon,
    LinkIcon,
    ArrowTopRightOnSquareIcon,
    XMarkIcon,
    GlobeAltIcon,
} from '@heroicons/vue/24/outline'

interface BranchLink {
    id: number
    url: string
    custom_name: string | null
    display_name: string
    position: number
}

interface TypeOfDisplay {
    value: string
    label: string
    color: string
}

interface Branch {
    id: number
    name: string
    logo: string | null
    is_global: boolean
    links: BranchLink[]
    type_of_display: TypeOfDisplay
}

const props = defineProps<{
    branches: Branch[]
}>()

const globalBranches = computed(() => props.branches.filter((b) => b.is_global))
const regularBranches = computed(() => props.branches.filter((b) => !b.is_global))

const selectedBranch = ref<Branch | null>(null)
const showModal = ref(false)

const openBranchModal = (branch: Branch) => {
    selectedBranch.value = branch
    showModal.value = true
}

const closeModal = () => {
    showModal.value = false
}
</script>

<template>
    <main
        class="relative mx-auto max-w-400 px-4 py-12 sm:px-6 lg:px-8 lg:pb-24"
        style="
            background-image: url('/images/map.png');
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
        "
    >
        <!-- Empty State -->
        <div
            v-if="branches.length === 0"
            class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 bg-zinc-200/30 px-6 py-24"
        >
            <div class="mb-4 rounded-full bg-zinc-300/50 p-4">
                <BuildingOffice2Icon class="h-12 w-12 text-zinc-500" />
            </div>
            <h3 class="text-lg font-medium text-zinc-700">No branches</h3>
            <p class="mt-1 text-sm text-zinc-500">Get started by adding your first branch in the admin panel.</p>
        </div>

        <template v-else>
            <!-- Global Branches Section -->
            <section
                v-if="globalBranches.length > 0"
                class="mb-12"
            >
                <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <button
                        v-for="branch in globalBranches"
                        :key="branch.id"
                        type="button"
                        @click="openBranchModal(branch)"
                        class="group relative overflow-hidden rounded-3xl border-2 border-white bg-white text-left shadow-xl transition-all duration-300 hover:border-amber-500/50 focus:ring-2 focus:ring-amber-500/50 focus:outline-none"
                    >
                        <!-- Card Header with Logo -->
                        <div
                            class="relative flex h-32 items-center justify-center overflow-hidden bg-linear-to-br from-amber-100/40 to-zinc-200"
                        >
                            <img
                                v-if="branch.logo"
                                :src="'/storage/' + branch.logo"
                                :alt="branch.name"
                                class="h-20 w-35 rounded-lg object-contain"
                            />
                            <div
                                v-else
                                class="flex h-40 w-40 items-center justify-center rounded-xl text-3xl font-bold text-zinc-700"
                            >
                                {{ branch.name.charAt(0).toUpperCase() }}
                            </div>

                            <!-- Global badge -->
                            <div
                                class="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-1 text-xs text-amber-600"
                            >
                                <GlobeAltIcon class="h-3 w-3" />
                                Global
                            </div>

                            <!-- Glow effect on hover -->
                            <div
                                class="absolute inset-0 bg-linear-to-t from-zinc-200 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                            />
                        </div>

                        <!-- Card Content -->
                        <div class="flex items-center justify-between px-2 py-2">
                            <h2
                                class="text-xs font-semibold text-zinc-800 transition-colors group-hover:text-amber-600"
                            >
                                {{ branch.name }}
                            </h2>

                            <!-- Click indicator -->
                            <div
                                class="flex items-center justify-center rounded-full bg-zinc-200 p-2 text-zinc-500 transition-all duration-300 group-hover:bg-amber-500/20 group-hover:text-amber-600"
                            >
                                <ChevronRightIcon class="size-4" />
                            </div>
                        </div>
                    </button>
                </div>
            </section>

            <!-- Regular Branches Section -->
            <section v-if="regularBranches.length > 0">
                <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <div
                        v-for="branch in regularBranches"
                        :key="branch.id"
                    >
                        <!-- Modal Display Type -->
                        <button
                            v-if="branch.type_of_display.value === 'in_modal'"
                            type="button"
                            @click="openBranchModal(branch)"
                            class="group relative w-full overflow-hidden rounded-3xl border-2 border-white bg-white text-left shadow-xl transition-all duration-300 hover:border-orange-500/50 hover:shadow-2xl hover:shadow-orange-500/10 focus:ring-2 focus:ring-orange-500/50 focus:outline-none"
                        >
                            <!-- Card Header with Logo -->
                            <div
                                class="relative flex h-32 items-center justify-center overflow-hidden bg-linear-to-br from-zinc-100 to-zinc-200"
                            >
                                <img
                                    v-if="branch.logo"
                                    :src="'/storage/' + branch.logo"
                                    :alt="branch.name"
                                    class="h-20 w-35 rounded-lg object-contain transition-transform duration-300 group-hover:scale-105"
                                />
                                <div
                                    v-else
                                    class="flex h-20 w-32 items-center justify-center text-3xl font-bold text-zinc-700"
                                >
                                    {{ branch.name.charAt(0).toUpperCase() }}
                                </div>

                                <!-- Glow effect on hover -->
                                <div
                                    class="absolute inset-0 bg-linear-to-t from-zinc-200 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                                />
                            </div>

                            <!-- Card Content -->
                            <div class="flex items-center justify-between px-3 py-2.5">
                                <h2
                                    class="text-sm font-semibold text-zinc-800 transition-colors group-hover:text-amber-600"
                                >
                                    {{ branch.name }}
                                </h2>

                                <div
                                    class="flex items-center justify-center rounded-full bg-zinc-100 p-2 text-zinc-400 transition-all duration-300 group-hover:bg-amber-500/20 group-hover:text-amber-600"
                                >
                                    <ChevronRightIcon class="h-4 w-4" />
                                </div>
                            </div>
                        </button>

                        <!-- Card Display Type - Shows Links Directly -->
                        <div
                            v-else
                            class="group relative w-full overflow-hidden rounded-3xl border-2 border-white bg-white shadow-xl transition-all duration-300 hover:shadow-2xl"
                        >
                            <!-- Card Title -->
                            <div class="border-b border-zinc-100 px-3 py-2.5">
                                <h2 class="text-center text-sm font-semibold text-zinc-800">
                                    {{ branch.name }}
                                </h2>
                            </div>

                            <!-- Links List -->
                            <div class="divide-y divide-zinc-100">
                                <a
                                    v-for="link in branch.links"
                                    :key="link.id"
                                    :href="link.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="group/link flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-sky-50/50"
                                >
                                    <LinkIcon
                                        class="h-4 w-4 shrink-0 text-zinc-400 transition-colors group-hover/link:text-sky-500"
                                    />
                                    <span
                                        class="min-w-0 flex-1 truncate text-sm text-zinc-600 transition-colors group-hover/link:text-zinc-900"
                                    >
                                        {{ link.display_name }}
                                    </span>
                                    <ArrowTopRightOnSquareIcon
                                        class="h-4 w-4 shrink-0 text-zinc-300 opacity-0 transition-all group-hover/link:text-sky-500 group-hover/link:opacity-100"
                                    />
                                </a>
                            </div>

                            <!-- Empty State -->
                            <div
                                v-if="branch.links.length === 0"
                                class="px-3 py-4 text-center text-sm text-zinc-400"
                            >
                                No links available
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </template>
    </main>

    <Modal
        :show="showModal"
        max-width="lg"
        @close="closeModal"
    >
        <div class="bg-zinc-100">
            <!-- Modal Header -->
            <div class="border-b border-zinc-200 bg-zinc-200 px-6 py-4">
                <div class="flex items-center gap-4">
                    <div
                        v-if="selectedBranch?.logo"
                        class="shrink-0"
                    >
                        <img
                            :src="'/storage/' + selectedBranch.logo"
                            :alt="selectedBranch.name"
                            class="h-12 w-12 object-contain"
                        />
                    </div>
                    <div
                        v-else
                        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-zinc-300 text-xl font-bold text-zinc-700"
                    >
                        {{ selectedBranch?.name.charAt(0).toUpperCase() }}
                    </div>
                    <div class="min-w-0 flex-1">
                        <h2 class="text-xl font-semibold text-zinc-800">
                            {{ selectedBranch?.name }}
                        </h2>
                        <p class="text-sm text-zinc-500">
                            {{ selectedBranch?.links.length }}
                            {{ selectedBranch?.links.length === 1 ? 'link' : 'links' }}
                        </p>
                    </div>
                    <button
                        type="button"
                        @click="closeModal"
                        class="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-300/50 hover:text-zinc-800"
                    >
                        <XMarkIcon class="h-5 w-5" />
                    </button>
                </div>
            </div>

            <!-- Modal Content - Links List -->
            <div class="max-h-140 overflow-y-auto px-4 py-3">
                <div
                    v-if="selectedBranch?.links.length"
                    class="divide-y divide-zinc-300/50"
                >
                    <a
                        v-for="link in selectedBranch.links"
                        :key="link.id"
                        :href="link.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="group/link flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-zinc-300/30"
                    >
                        <LinkIcon class="h-4 w-4 shrink-0 text-zinc-500 group-hover/link:text-sky-500" />
                        <span class="min-w-0 flex-1 truncate text-sm text-zinc-700 group-hover/link:text-zinc-900">
                            {{ link.display_name }}
                        </span>
                        <ArrowTopRightOnSquareIcon
                            class="h-4 w-4 shrink-0 text-zinc-400 opacity-0 transition-all group-hover/link:text-sky-500 group-hover/link:opacity-100"
                        />
                    </a>
                </div>

                <!-- No Links State -->
                <div
                    v-else
                    class="flex flex-col items-center justify-center py-12"
                >
                    <div class="mb-3 rounded-full bg-zinc-300/50 p-3">
                        <LinkIcon class="h-8 w-8 text-zinc-500" />
                    </div>
                    <p class="text-zinc-500">No links</p>
                </div>
            </div>
        </div>
    </Modal>
</template>
